import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, CreditCard, Heart, Inbox, MessageSquare, PackageSearch, RefreshCw, ShoppingCart, TrendingUp, Truck, Users, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StatCard } from '@/components';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import { dashboardApi } from '@/services/dashboardApi';
import type { DashboardProductConversionMetric, DashboardProductMetric, DashboardRecentUser, DashboardResponse } from '@/types/dashboard';
import type { NotificationItem } from '@/types/notification';

type PerformanceRange = 'weekly' | 'monthly';
type ProductListMode = 'mostReviewed' | 'mostFavorited';

interface EmptyStateProps {
  title: string;
  description: string;
}

interface MetricMiniCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  tone: 'blue' | 'amber' | 'red' | 'green';
  onClick?: () => void;
}

const formatCurrency = (amount: number) => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 0,
}).format(amount || 0);

const formatCompactNumber = (value: number) => new Intl.NumberFormat('tr-TR').format(value || 0);

const formatDateTime = (value: string | Date | null | undefined) => {
  if (!value) {
    return '-';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatRelativeTime = (value: string | Date | null | undefined) => {
  if (!value) {
    return '-';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const diffMs = date.getTime() - Date.now();
  const formatter = new Intl.RelativeTimeFormat('tr-TR', { numeric: 'auto' });
  const minutes = Math.round(diffMs / 60000);

  if (Math.abs(minutes) < 60) {
    return formatter.format(minutes, 'minute');
  }

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) {
    return formatter.format(hours, 'hour');
  }

  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) {
    return formatter.format(days, 'day');
  }

  const months = Math.round(days / 30);
  return formatter.format(months, 'month');
};

const getUserInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const buildEmptyDashboard = (): DashboardResponse => ({
  summary: {
    totalOrderAmount: 0,
    totalOrderCount: 0,
    currentMonthOrderAmount: 0,
    currentMonthOrderCount: 0,
    unreadNotificationCount: 0,
  },
  operations: {
    unassignedVendorCount: 0,
    unassignedCourierCount: 0,
    previewPendingCount: 0,
    cancelledOrderCount: 0,
  },
  deliveries: {
    todayDeliveryCount: 0,
    overdueDeliveryCount: 0,
    onTheWayTodayCount: 0,
  },
  payments: {
    awaitingPaymentCount: 0,
    paidNotProcessedCount: 0,
    failedPaymentCount: 0,
  },
  recentUsers: [],
  recentNotifications: [],
  orderPerformance: {
    weekly: [],
    monthly: [],
  },
  topProducts: {
    mostReviewed: [],
    mostFavorited: [],
  },
  productConversionPerformance: [],
  categoryProductCounts: [],
});

const normalizeDashboardResponse = (input: Partial<DashboardResponse> | null | undefined): DashboardResponse => {
  const empty = buildEmptyDashboard();

  return {
    summary: {
      ...empty.summary,
      ...(input?.summary || {}),
    },
    operations: {
      ...empty.operations,
      ...(input?.operations || {}),
    },
    deliveries: {
      ...empty.deliveries,
      ...(input?.deliveries || {}),
    },
    payments: {
      ...empty.payments,
      ...(input?.payments || {}),
    },
    recentUsers: Array.isArray(input?.recentUsers) ? input!.recentUsers : empty.recentUsers,
    recentNotifications: Array.isArray(input?.recentNotifications) ? input!.recentNotifications : empty.recentNotifications,
    orderPerformance: {
      weekly: Array.isArray(input?.orderPerformance?.weekly) ? input!.orderPerformance!.weekly : empty.orderPerformance.weekly,
      monthly: Array.isArray(input?.orderPerformance?.monthly) ? input!.orderPerformance!.monthly : empty.orderPerformance.monthly,
    },
    topProducts: {
      mostReviewed: Array.isArray(input?.topProducts?.mostReviewed) ? input!.topProducts!.mostReviewed : empty.topProducts.mostReviewed,
      mostFavorited: Array.isArray(input?.topProducts?.mostFavorited) ? input!.topProducts!.mostFavorited : empty.topProducts.mostFavorited,
    },
    productConversionPerformance: Array.isArray(input?.productConversionPerformance)
      ? input!.productConversionPerformance
      : empty.productConversionPerformance,
    categoryProductCounts: Array.isArray(input?.categoryProductCounts)
      ? input!.categoryProductCounts
      : empty.categoryProductCounts,
  };
};

const SectionEmptyState: React.FC<EmptyStateProps> = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 py-8 text-center">
    <Inbox className="mx-auto text-gray-400" size={28} />
    <p className="mt-3 text-sm font-semibold text-dark dark:text-light">{title}</p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
  </div>
);

const metricToneClasses: Record<MetricMiniCardProps['tone'], string> = {
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-200',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200',
  green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-200',
};

const MetricMiniCard: React.FC<MetricMiniCardProps> = ({ title, value, icon, tone, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-left transition hover:border-primary/40 hover:shadow-sm ${onClick ? 'cursor-pointer' : 'cursor-default'} `}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="mt-2 text-2xl font-bold text-dark dark:text-light">{formatCompactNumber(value)}</p>
      </div>
      <div className={`rounded-lg p-2 ${metricToneClasses[tone]}`}>
        {icon}
      </div>
    </div>
  </button>
);

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div>
      <div className="h-8 w-44 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-3 h-4 w-80 max-w-full rounded bg-gray-200 dark:bg-gray-700" />
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="card space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-32 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="h-12 w-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="card lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-5 w-40 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-56 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="h-9 w-32 rounded-lg bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="h-[300px] rounded-lg bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="card space-y-3">
        <div className="h-5 w-36 rounded bg-gray-200 dark:bg-gray-700" />
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="space-y-2 border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0">
            <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      {[0, 1].map((item) => (
        <div key={item} className="card space-y-3">
          <div className="h-5 w-40 rounded bg-gray-200 dark:bg-gray-700" />
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex items-center gap-3 border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0">
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-48 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardResponse>(buildEmptyDashboard);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [performanceRange, setPerformanceRange] = useState<PerformanceRange>('weekly');
  const [productListMode, setProductListMode] = useState<ProductListMode>('mostReviewed');

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const data = await dashboardApi.getDashboard();
      setDashboard(normalizeDashboardResponse(data));
    } catch (error: any) {
      const message = error.message || 'Dashboard verileri yüklenemedi';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const performanceData = dashboard.orderPerformance[performanceRange] || [];
  const productData = dashboard.topProducts[productListMode] || [];

  const monthAmountChange = useMemo(() => {
    const monthly = dashboard.orderPerformance.monthly;
    if (monthly.length < 2) {
      return 0;
    }

    const current = monthly[monthly.length - 1]?.totalAmount || 0;
    const previous = monthly[monthly.length - 2]?.totalAmount || 0;
    if (previous === 0) {
      return current > 0 ? 100 : 0;
    }

    return Math.round(((current - previous) / previous) * 100);
  }, [dashboard.orderPerformance.monthly]);

  const monthOrderCountChange = useMemo(() => {
    const monthly = dashboard.orderPerformance.monthly;
    if (monthly.length < 2) {
      return 0;
    }

    const current = monthly[monthly.length - 1]?.orderCount || 0;
    const previous = monthly[monthly.length - 2]?.orderCount || 0;
    if (previous === 0) {
      return current > 0 ? 100 : 0;
    }

    return Math.round(((current - previous) / previous) * 100);
  }, [dashboard.orderPerformance.monthly]);

  const unreadChange = useMemo(() => {
    return dashboard.summary.unreadNotificationCount > 0 ? dashboard.summary.unreadNotificationCount : 0;
  }, [dashboard.summary.unreadNotificationCount]);

  const isCompletelyEmpty = useMemo(() => (
    dashboard.summary.totalOrderAmount === 0 &&
    dashboard.summary.totalOrderCount === 0 &&
    dashboard.summary.currentMonthOrderAmount === 0 &&
    dashboard.summary.currentMonthOrderCount === 0 &&
    dashboard.summary.unreadNotificationCount === 0 &&
    dashboard.recentUsers.length === 0 &&
    dashboard.recentNotifications.length === 0 &&
    dashboard.orderPerformance.weekly.length === 0 &&
    dashboard.orderPerformance.monthly.length === 0 &&
    dashboard.topProducts.mostReviewed.length === 0 &&
    dashboard.topProducts.mostFavorited.length === 0 &&
    dashboard.productConversionPerformance.length === 0 &&
    dashboard.categoryProductCounts.length === 0
  ), [dashboard]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="card py-14 text-center">
        <AlertTriangle className="mx-auto text-red-500" size={36} />
        <h2 className="mt-4 text-xl font-semibold text-dark dark:text-light">Dashboard yüklenemedi</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{errorMessage}</p>
        <button
          type="button"
          onClick={loadDashboard}
          className="btn btn-primary mt-6 inline-flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Yönetim özetini gerçek verilerle canlı olarak takip edin.</p>
        </div>
        <button
          type="button"
          onClick={loadDashboard}
          className="btn btn-secondary inline-flex items-center gap-2 self-start"
        >
          <RefreshCw size={16} />
          Yenile
        </button>
      </div>

      {isCompletelyEmpty && (
        <div className="card py-14 text-center">
          <Inbox className="mx-auto text-gray-400" size={36} />
          <h2 className="mt-4 text-xl font-semibold text-dark dark:text-light">Dashboard için henüz veri yok</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sipariş, kullanıcı, bildirim veya ürün verisi oluştuğunda bu alanlar otomatik dolacak.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Toplam Sipariş Tutarı"
          value={formatCurrency(dashboard.summary.totalOrderAmount)}
          change={monthAmountChange}
          icon={<ShoppingCart size={24} />}
          color="blue"
          onClick={() => navigate('/admin/orders')}
        />
        <StatCard
          title="Bu Ay Sipariş Tutarı"
          value={formatCurrency(dashboard.summary.currentMonthOrderAmount)}
          change={monthAmountChange}
          icon={<TrendingUp size={24} />}
          color="green"
          onClick={() => navigate('/admin/accounting')}
        />
        <StatCard
          title="Bu Ay Sipariş Adedi"
          value={formatCompactNumber(dashboard.summary.currentMonthOrderCount)}
          change={monthOrderCountChange}
          icon={<Users size={24} />}
          color="yellow"
          onClick={() => navigate('/admin/orders')}
        />
        <StatCard
          title="Okunmamış Bildirim"
          value={formatCompactNumber(dashboard.summary.unreadNotificationCount)}
          change={unreadChange}
          icon={<Bell size={24} />}
          color="red"
          onClick={() => navigate('/admin/notifications')}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-dark dark:text-light">Operasyon Kuyruğu</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Atama ve önizleme bekleyen sipariş özeti</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MetricMiniCard title="Vendor Atanmamış" value={dashboard.operations.unassignedVendorCount} icon={<PackageSearch size={18} />} tone="amber" onClick={() => navigate('/admin/orders')} />
            <MetricMiniCard title="Kurye Atanmamış" value={dashboard.operations.unassignedCourierCount} icon={<Truck size={18} />} tone="blue" onClick={() => navigate('/admin/orders')} />
            <MetricMiniCard title="Önizleme Bekleyen" value={dashboard.operations.previewPendingCount} icon={<Eye size={18} />} tone="green" onClick={() => navigate('/admin/orders')} />
            <MetricMiniCard title="İptal Edilen" value={dashboard.operations.cancelledOrderCount} icon={<AlertTriangle size={18} />} tone="red" onClick={() => navigate('/admin/orders')} />
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-dark dark:text-light">Bugün Teslimat</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Bugüne ait teslimat ve gecikme görünümü</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1">
            <MetricMiniCard title="Bugün Teslim" value={dashboard.deliveries.todayDeliveryCount} icon={<ShoppingCart size={18} />} tone="blue" onClick={() => navigate('/admin/orders')} />
            <MetricMiniCard title="Geciken" value={dashboard.deliveries.overdueDeliveryCount} icon={<AlertTriangle size={18} />} tone="red" onClick={() => navigate('/admin/orders')} />
            <MetricMiniCard title="Yolda" value={dashboard.deliveries.onTheWayTodayCount} icon={<Truck size={18} />} tone="green" onClick={() => navigate('/admin/orders')} />
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-dark dark:text-light">Ödeme Durumu</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Bekleyen, işlenmeyen ve başarısız ödemeler</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1">
            <MetricMiniCard title="Ödeme Bekleyen" value={dashboard.payments.awaitingPaymentCount} icon={<CreditCard size={18} />} tone="amber" onClick={() => navigate('/admin/accounting/orders')} />
            <MetricMiniCard title="Alındı Ama İşlenmedi" value={dashboard.payments.paidNotProcessedCount} icon={<Bell size={18} />} tone="blue" onClick={() => navigate('/admin/accounting/orders')} />
            <MetricMiniCard title="Başarısız Ödeme" value={dashboard.payments.failedPaymentCount} icon={<AlertTriangle size={18} />} tone="red" onClick={() => navigate('/admin/accounting/orders')} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-dark dark:text-light">Sipariş Performansı</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Haftalık ve aylık görünüm arasında geçiş yapabilirsiniz.</p>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1">
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${performanceRange === 'weekly' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}
                onClick={() => setPerformanceRange('weekly')}
              >
                Haftalık
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${performanceRange === 'monthly' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}
                onClick={() => setPerformanceRange('monthly')}
              >
                Aylık
              </button>
            </div>
          </div>

          {performanceData.length === 0 ? (
            <SectionEmptyState
              title="Grafik verisi yok"
              description="Seçili periyot için sipariş performansı oluşmamış görünüyor."
            />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'Ciro (₺)') {
                      return [formatCurrency(value), name];
                    }

                    return [formatCompactNumber(value), name];
                  }}
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
                <Legend />
                <Bar dataKey="orderCount" fill="#2563eb" name="Sipariş" radius={[6, 6, 0, 0]} />
                <Bar dataKey="totalAmount" fill="#16a34a" name="Ciro (₺)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-dark dark:text-light">Son 10 Bildirim</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sisteme düşen son hareketler</p>
            </div>
            <span className="badge badge-info">{dashboard.recentNotifications.length}</span>
          </div>
          <div className="space-y-3">
            {dashboard.recentNotifications.length === 0 ? (
              <SectionEmptyState
                title="Bildirim yok"
                description="Sisteme henüz yeni bir bildirim düşmemiş görünüyor."
              />
            ) : dashboard.recentNotifications.map((notification: NotificationItem) => (
              <button key={notification.id} type="button" onClick={() => navigate('/admin/notifications')} className="w-full border-b border-gray-200 dark:border-gray-700 pb-3 text-left last:border-b-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-dark dark:text-light truncate">{notification.title}</p>
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{notification.body}</p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${notification.isRead ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'}`}>
                    {notification.isRead ? 'Okundu' : 'Yeni'}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{notification.recipientRole}</span>
                  <span>{formatRelativeTime(notification.createdAt)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-dark dark:text-light">Son 10 Kullanıcı</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">En son kayıt olan müşteriler</p>
            </div>
            <span className="badge badge-info">{dashboard.recentUsers.length}</span>
          </div>
          <div className="space-y-3">
            {dashboard.recentUsers.length === 0 ? (
              <SectionEmptyState
                title="Kullanıcı kaydı yok"
                description="Henüz müşteri kaydı oluşmadığı için bu liste boş görünüyor."
              />
            ) : dashboard.recentUsers.map((user: DashboardRecentUser) => (
              <button key={user.id} type="button" onClick={() => navigate(`/admin/customers/${user.id}`)} className="flex w-full items-center gap-3 border-b border-gray-200 dark:border-gray-700 pb-3 text-left last:border-b-0 last:pb-0">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {getUserInitials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-dark dark:text-light truncate">{user.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${user.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200' : user.status === 'blocked' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {user.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{user.email || user.phone || '-'}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium text-dark dark:text-light">{user.totalOrders} sipariş</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">{formatDateTime(user.createdAt)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="card space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-dark dark:text-light">Öne Çıkan Ürünler</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Yorum ve beğeni performansına göre ilk 10 ürün</p>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1">
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${productListMode === 'mostReviewed' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}
                onClick={() => setProductListMode('mostReviewed')}
              >
                En Çok Yorum
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${productListMode === 'mostFavorited' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}
                onClick={() => setProductListMode('mostFavorited')}
              >
                En Çok Beğeni
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {productData.length === 0 ? (
              <SectionEmptyState
                title="Ürün sıralaması yok"
                description="Yorum veya beğeni verisi oluştuğunda bu sıralama dolacak."
              />
            ) : productData.map((product: DashboardProductMetric, index: number) => (
              <button key={product.id} type="button" onClick={() => navigate(`/admin/products/${product.id}/edit`)} className="flex w-full items-center gap-3 border-b border-gray-200 dark:border-gray-700 pb-3 text-left last:border-b-0 last:pb-0">
                <div className="w-8 text-sm font-bold text-gray-500 dark:text-gray-400">#{index + 1}</div>
                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MessageSquare size={18} />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-dark dark:text-light truncate">{product.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {product.categories.join(', ') || 'Kategori yok'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center justify-end gap-1 text-xs text-gray-600 dark:text-gray-300">
                    <MessageSquare size={14} />
                    <span>{product.reviewCount}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-end gap-1 text-xs text-gray-600 dark:text-gray-300">
                    <Heart size={14} />
                    <span>{product.favoriteCount}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-dark dark:text-light">Kategoriye Göre Ürün Adetleri</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Her kategoride kaç ürün bulunduğu</p>
          </div>
          <span className="badge badge-info">{dashboard.categoryProductCounts.length} kategori</span>
        </div>

        {dashboard.categoryProductCounts.length === 0 ? (
          <SectionEmptyState
            title="Kategori dağılımı yok"
            description="Kategori ve ürün verisi eşleştiğinde burada dağılım gösterilecek."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {dashboard.categoryProductCounts.map((category) => (
              <button key={category.categoryId} type="button" onClick={() => navigate('/admin/taxonomy')} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-left transition hover:border-primary/40 hover:shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-dark dark:text-light truncate">{category.categoryName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{category.slug}</p>
                  </div>
                  <span className="text-lg font-bold text-primary">{category.productCount}</span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{
                      width: `${Math.max(8, (category.productCount / Math.max(...dashboard.categoryProductCounts.map((item) => item.productCount), 1)) * 100)}%`,
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-dark dark:text-light">Ürün Görüntülenme ve Satın Alma Performansı</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Tekil görüntülenme, sipariş ve web-mobil dağılımı</p>
          </div>
          <span className="badge badge-info">{dashboard.productConversionPerformance.length} ürün</span>
        </div>

        {dashboard.productConversionPerformance.length === 0 ? (
          <SectionEmptyState
            title="Henüz ürün analitiği yok"
            description="Deploy sonrası ürün detay açılışları ve yeni siparişler geldikçe bu tablo dolacak."
          />
        ) : (
          <div className="space-y-3">
            {dashboard.productConversionPerformance.map((product: DashboardProductConversionMetric, index: number) => (
              <button key={product.productId} type="button" onClick={() => navigate(`/admin/products/${product.productId}/edit`)} className="w-full rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-left transition hover:border-primary/40 hover:shadow-sm">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 text-sm font-bold text-gray-500 dark:text-gray-400">#{index + 1}</div>
                    <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Eye size={18} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-dark dark:text-light truncate">{product.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Dönüşüm: %{product.conversionRate.toFixed(1)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Tekil Görüntülenme</p>
                      <p className="mt-1 text-sm font-semibold text-dark dark:text-light">{formatCompactNumber(product.totalUniqueViews)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Sipariş</p>
                      <p className="mt-1 text-sm font-semibold text-dark dark:text-light">{formatCompactNumber(product.totalOrderCount)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Satılan Adet</p>
                      <p className="mt-1 text-sm font-semibold text-dark dark:text-light">{formatCompactNumber(product.totalOrderedQuantity)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Öne Çıkan Kanal</p>
                      <p className="mt-1 text-sm font-semibold text-dark dark:text-light">
                        {product.webUniqueViews + product.webOrderCount >= product.mobileUniqueViews + product.mobileOrderCount ? 'Web' : 'Mobil'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3">
                    <p className="text-xs font-semibold text-dark dark:text-light">Web</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatCompactNumber(product.webUniqueViews)} görüntülenme, {formatCompactNumber(product.webOrderCount)} sipariş
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3">
                    <p className="text-xs font-semibold text-dark dark:text-light">Mobil</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatCompactNumber(product.mobileUniqueViews)} görüntülenme, {formatCompactNumber(product.mobileOrderCount)} sipariş
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
